import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Account from "@mui/icons-material/AccountCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import EmailValidator from "email-validator";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "@/common/axios";
import type { Person } from "./repositories/common";

const VALIDATIONS = {
  nameCheck: false,
  emailCheck: false,
  typeCheck: false,
};

const personTypeTranslationKey = (type: Person["type"]) =>
  type === "Author" ? "export.repositories.user.author" : "export.repositories.user.contact";

function AdditionalUserDialog({
  onAddUser,
  onClose,
  open,
}: {
  onAddUser: (person: Person) => void;
  onClose: () => void;
  open: boolean;
}) {
  const { t } = useTranslation(["workspace", "common"]);
  const [contactsName, setContactsName] = useState("");
  const [contactsType, setContactsType] = useState<"" | "Author" | "Contact">("");
  const [contactsEmail, setContactsEmail] = useState("");

  /*
   * This state variable is set to true once the user has attempted to add a
   * new user to their repository export options, but has been prevented from
   * doing so due to an error.
   */
  const [addPersonDialogSubmitAttempt, setAddPersonDialogSubmitAttempt] = useState(false);

  const [validations, setValidations] = useState(VALIDATIONS);

  const handleClose = () => {
    setAddPersonDialogSubmitAttempt(false);
    setContactsName("");
    setContactsEmail("");
    setContactsType("");
    setValidations(VALIDATIONS);
    onClose();
  };

  const validateDetails = (details: Person) => {
    const newValidations = VALIDATIONS;

    if (/(.*[a-z]){3}/i.test(details.uniqueName)) newValidations.nameCheck = true;
    if (EmailValidator.validate(details.email)) newValidations.emailCheck = true;
    if (details.type !== "") newValidations.typeCheck = true;

    setValidations(newValidations);
    setAddPersonDialogSubmitAttempt(true);

    return Object.values(newValidations).every((b) => b);
  };

  const handleAddPerson = () => {
    const details = {
      uniqueName: contactsName,
      email: contactsEmail,
      type: contactsType,
    };
    if (validateDetails(details)) {
      onAddUser(details);
      handleClose();
    }
  };

  return (
    <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={open} maxWidth={"md"}>
      <DialogTitle>{t("export.repositories.user.addDialogTitle")}</DialogTitle>
      <DialogContent>
        <FormControl error aria-describedby="name-error-text" fullWidth>
          <TextField
            variant="standard"
            error={addPersonDialogSubmitAttempt && !validations.nameCheck}
            name="contactsName"
            label={t("export.repositories.user.name")}
            required
            value={contactsName}
            onChange={({ target: { value } }) => setContactsName(value)}
            margin="normal"
            data-test-id="user-name"
          />
        </FormControl>
        <FormControl error aria-describedby="email-error-text" fullWidth>
          <TextField
            variant="standard"
            error={addPersonDialogSubmitAttempt && !validations.emailCheck}
            name="contactsEmail"
            label={t("export.repositories.user.email")}
            required
            value={contactsEmail}
            onChange={({ target: { value } }) => setContactsEmail(value)}
            margin="normal"
            data-test-id="user-email"
          />
        </FormControl>
        <FormControl fullWidth error aria-describedby="type-error-text">
          <TextField
            variant="standard"
            error={addPersonDialogSubmitAttempt && !validations.typeCheck}
            name="contactsType"
            select
            label={t("export.repositories.user.type")}
            required
            value={contactsType}
            onChange={({ target: { value } }) => {
              if (value === "" || value === "Author" || value === "Contact") {
                setContactsType(value);
                return;
              }
              throw new Error("Invalid type");
            }}
            margin="normal"
            data-test-id="user-type"
          >
            <MenuItem value="Author" data-test-id="user-type-author">
              {t("export.repositories.user.author")}
            </MenuItem>
            <MenuItem value="Contact" data-test-id="user-type-contact">
              {t("export.repositories.user.contact")}
            </MenuItem>
          </TextField>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" data-test-id="button-cancel">
          {t("common:actions.cancel")}
        </Button>
        <Button onClick={handleAddPerson} color="primary" data-test-id="button-add">
          {t("common:actions.add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type ExportRepoUserArgs = {
  submitAttempt: boolean;
  inputValidations: { author: boolean; contact: boolean };
  initialPeople: Array<Person>;
  updatePeople: (contactAndAuthor: Array<Person>) => void;
};

/*
 * This component a widget for selecting another user who should be associated
 * with the export to a repository, either because they are an author of the
 * work or are some other noteworthy point of contact.
 */
export default function ExportRepoUser({
  /*
   * This prop is renamed to make it clear that it is distinct from
   * addPersonDialogSubmitAttempt. This is set to true once the user has
   * attempted to submit the whole export dialog but has been prevented from
   * doing so due to an error.
   */
  submitAttempt: exportDialogSubmitAttempt,

  inputValidations,
  initialPeople,
  updatePeople,
}: ExportRepoUserArgs): React.ReactNode {
  const { t } = useTranslation(["workspace", "common"]);
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState(initialPeople);

  const getCurrentUser = () => {
    void axios.get<{ fullName: string; email: string }>("/directory/ajax/subject").then((response) => {
      const user = response.data;
      const currentUserRoles = [
        { uniqueName: user.fullName, email: user.email, type: "Author" },
        { uniqueName: user.fullName, email: user.email, type: "Contact" },
      ];
      setPeople(currentUserRoles);
      updatePeople(currentUserRoles);
    });
  };

  useEffect(() => {
    if (initialPeople.length === 0) getCurrentUser();
  }, []);

  const handleDeletePerson = (index: number) => {
    const newPeople = people.toSpliced(index, 1);
    setPeople(newPeople);
    updatePeople(newPeople);
  };

  return (
    <Grid container>
      <Grid sx={{ mt: 2.5 }} size={12}>
        <Grid container sx={{ display: "flex" }}>
          <Grid sx={{ flexGrow: 1 }}>
            <FormLabel
              error={exportDialogSubmitAttempt && (!inputValidations.author || !inputValidations.contact)}
              component="legend"
            >
              {t("export.repositories.user.peopleLabel")}
            </FormLabel>
            <FormHelperText
              error={exportDialogSubmitAttempt && (!inputValidations.author || !inputValidations.contact)}
            >
              {t("export.repositories.user.peopleHelper")}
            </FormHelperText>
          </Grid>
          <Tooltip title={t("export.repositories.user.addTooltip")} sx={{ flexGrow: 0, width: "auto" }}>
            <IconButton onClick={() => setOpen(true)} data-test-id="add-user">
              <FontAwesomeIcon icon={faUserPlus} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <AdditionalUserDialog
        open={open}
        onClose={() => setOpen(false)}
        onAddUser={(person) => {
          setPeople([...people, person]);
          updatePeople([...people, person]);
        }}
      />
      <Grid size={12}>
        <List>
          {people.map((option, index) => (
            <ListItem key={option.email + option.type}>
              <ListItemAvatar>
                <Avatar>
                  <Account />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`${option.uniqueName} - ${t(personTypeTranslationKey(option.type))}`}
                secondary={option.email}
              />
              <ListItemSecondaryAction>
                <IconButton
                  aria-label={t("common:actions.delete")}
                  onClick={() => handleDeletePerson(index)}
                  data-test-id="button-delete-user"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Grid>
    </Grid>
  );
}
